/** Axios error shape as the backend's HttpExceptionFilter returns it. */
export type ApiError = Error & {
  response?: { status?: number; data?: { message?: string | string[] } };
};

/**
 * The backend already sends a Serbian message for every 4xx it raises, so a
 * mutation's `onError` should show that rather than a generic failure. Falls
 * back to the caller's own translated string when the server sent nothing
 * (network error, 5xx).
 *
 * Validation failures come back as an array (one entry per failed constraint),
 * so those are joined instead of dropped to the fallback.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const response = (error as ApiError)?.response;
  // A 5xx body is the framework's English text ("Internal server error"),
  // never a message written for the user.
  if (!response || (response.status ?? 0) >= 500) return fallback;
  const message = response.data?.message;
  if (Array.isArray(message)) {
    const joined = message.filter((m) => typeof m === 'string' && m.length > 0).join(' ');
    return joined.length > 0 ? joined : fallback;
  }
  return typeof message === 'string' && message.length > 0 ? message : fallback;
}
